/**
 * IndexedDB cache service for API responses
 * Provides caching with TTL support for improved performance
 */

interface CacheEntry<T = any> {
  key: string;
  data: T;
  timestamp: number;
  ttl: number;
}

class Cache {
  private dbName = "crux-api-cache";
  private version = 1;
  private storeName = "cache-entries";
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  private async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "key" });
        }
      };
    });

    return this.initPromise;
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error("Failed to initialize IndexedDB");
    }
    return this.db;
  }

  generateKey(
    endpoint: string,
    params?: Record<string, any>,
    accountId?: string,
  ): string {
    const paramString = params ? JSON.stringify(params) : "";
    const key = `${endpoint}${paramString}${accountId ? `_${accountId}` : ""}`;
    return btoa(unescape(encodeURIComponent(key))).replace(/[/+=]/g, "_");
  }

  async set<T>(key: string, data: T, ttl: number = 24 * 60 * 60 * 1000): Promise<void> {
    const db = await this.ensureDB();

    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: Date.now(),
      ttl,
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(entry);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        reject(new Error(`Failed to store cache entry: ${request.error}`));
      };
    });
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as CacheEntry<T> | undefined;

        if (!result) {
          resolve(null);
          return;
        }

        // Check if entry has expired
        const now = Date.now();
        if (now - result.timestamp > result.ttl) {
          // Entry expired, delete it and return null
          void this.delete(key);
          resolve(null);
          return;
        }

        resolve(result.data);
      };

      request.onerror = () => {
        reject(new Error(`Failed to retrieve cache entry: ${request.error}`));
      };
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        reject(new Error(`Failed to delete cache entry: ${request.error}`));
      };
    });
  }
}

// Export singleton instance
export const cache = new Cache();
